import axios from "axios";
import { refreshTokenService } from "./auth.service";
import { decodeJWT } from "../utils/jwt";
import { clearAllUserData } from "../utils/auth-utils";

// Set config defaults when creating the instance
export const axiosObject = axios.create();

// Create a separate axios instance
export const authAxios = axios.create();

// Utility to check if a URL is an auth endpoint (not business logic)
function isAuthEndpoint(url: string) {
  return (
    url.includes('/login') ||
    url.includes('/user/refresh-token') ||
    url.includes('/user/forgot-password') ||
    url.includes('/user/reset-password') ||
    url.includes('/user/signup-with-organization') ||
    url.includes('/user/send-otp') ||
    url.includes('/user/verify-otp') ||
    url.includes('/user/invite/')
  );
}


function isPublicPage() {
  const currentPath = window.location.pathname;
  return (
    currentPath === '/login' ||
    currentPath === '/signup' ||
    currentPath === '/forgot-password' ||
    currentPath === '/reset-password' ||
    currentPath === '/' ||
    currentPath.startsWith('/redeem-invite/') ||
    // Payment return pages — do not redirect to login during verify flow
    currentPath === '/subscription/success' ||
    currentPath.includes('/subscription/success')
  );
}

/**
 * Shared refresh helper used by both the request and response interceptors.
 * Uses a single in-flight promise ("single-flight" pattern) instead of a
 * boolean flag + manual queue: the promise reference itself is assigned
 * synchronously on the first call, so any number of concurrent callers
 * (no matter how they interleave) are guaranteed to await the exact same
 * refresh call and never trigger a second, racing refresh-token request.
 */
let refreshPromise: Promise<string> | null = null;

export function refreshWithQueue(): Promise<string> {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = refreshAccessToken()
    .catch((error) => {
      const status = (error as any)?.response?.status;
      if (status === 400 || status === 401 || status === 403) {
        // On payment-return pages, keep existing tokens so verify can still run.
        // The user already paid — clearing auth here would cause a 401 on verify.
        if (!isPublicPage()) {
          clearAuthData();
          window.location.href = '/login';
        }
      }
      throw error;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
}

// Request interceptor
axiosObject.interceptors.request.use(
  async function (config) {
    // Skip token for auth endpoints and OTP endpoints
    if (
      config.url?.includes("/login") ||
      config.url?.includes("user/forgot-password") ||
      config.url?.includes("user/reset-password") ||
      config.url?.includes("user/signup-with-organization") ||
      config.url?.includes("user/send-otp") ||
      config.url?.includes("user/verify-otp") ||
      config.url?.includes("user/invite/") ||
      config.url?.includes("/subscription/verify-public") ||
      config.url?.includes("/subscription/plans")
    ) {
      return config;
    }

    const accessToken = localStorage.getItem("accessToken");
    const refreshToken = localStorage.getItem("refreshToken");
    
    if (accessToken) {
      try {
        const decodedToken = JSON.parse(atob(accessToken.split('.')[1] || ''));
        const expirationTime = decodedToken.exp * 1000;
        
        if (expirationTime && Date.now() >= expirationTime) {
          if (refreshToken) {
            const newToken = await refreshWithQueue();
            config.headers.Authorization = `Bearer ${newToken}`;
          } else {
            return Promise.reject(new Error('No refresh token available'));
          }
        } else {
          config.headers.Authorization = `Bearer ${accessToken}`;
        }
      } catch (decodeError) {
        if (refreshToken) {
          try {
            const newToken = await refreshWithQueue();
            config.headers.Authorization = `Bearer ${newToken}`;
          } catch (refreshError) {
            return Promise.reject(refreshError);
          }
        } else {
          return Promise.reject(decodeError);
        }
      }
    } else if (refreshToken) {
      try {
        const newToken = await refreshWithQueue();
        config.headers.Authorization = `Bearer ${newToken}`;
      } catch (refreshError) {
        return Promise.reject(refreshError);
      }
    } else {
      if (!isPublicPage()) {
        return Promise.reject(new Error('No authentication tokens available'));
      }
      return config;
    }
    
    return config;
  },
  function (error) {
    return Promise.reject(error);
  }
);

// Response interceptor
axiosObject.interceptors.response.use(
  function (response) {
    return response;
  },
  async function (error) {
    const originalRequest = error.config;
    
    // Only handle 401 for non-auth endpoints (i.e., business endpoints)
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !isAuthEndpoint(originalRequest.url || '')
    ) {
      // Mark immediately (before any await) so this same request can never
      // re-enter this branch and trigger a duplicate refresh cycle, whether
      // it's the caller that starts the refresh or one that just awaits it.
      originalRequest._retry = true;

      const refreshToken = localStorage.getItem("refreshToken");

      if (!refreshToken) {
        clearAuthData();
        if (!isPublicPage()) {
          window.location.href = "/login";
        }
        return Promise.reject(error);
      }

      try {
        const newToken = await refreshWithQueue();
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return axiosObject(originalRequest);
      } catch (refreshError) {
        return Promise.reject(refreshError);
      }
    }
    
    return Promise.reject(error);
  }
);

async function refreshAccessToken() {
  const refreshToken = localStorage.getItem("refreshToken");
  if (!refreshToken) {
    throw new Error("No refresh token available");
  }
  try {
    const response = await refreshTokenService(refreshToken);
    const userData = decodeJWT(response.access_token);
    localStorage.setItem("accessToken", response.access_token);
    localStorage.setItem("refreshToken", response.refresh_token);
    localStorage.setItem("expirationTime", `${userData.exp * 1000}`);
    return response.access_token;
  } catch (error) {
    throw error;
  }
}

function clearAuthData() {
  clearAllUserData();
}

export default axiosObject; 